import { useState, useEffect, useCallback } from 'react';
import api, { API } from '../lib/api';
import { FileText, Download, ExternalLink, RefreshCw, UtensilsCrossed } from 'lucide-react';

const PDF_URL = `${API}/tageskarte/current.pdf`;

function formatUpdated(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      weekday: 'long', day: '2-digit', month: 'long',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export default function TageskartePage() {
  const [data, setData] = useState(null);
  const [brand, setBrand] = useState({ logo_text: 'La Contadina' });
  const [loading, setLoading] = useState(true);
  // cache-buster so the embedded PDF refreshes when a new one is published
  const [bust, setBust] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const r = await api.get('/tageskarte/current');
      setData(r.data);
      setBust(Date.now());
    } catch {
      setData({ available: false, message: 'Die Tageskarte wird gerade aktualisiert.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get('/settings/public').then(r => setBrand(r.data || {})).catch(() => {});
  }, [load]);

  // Auto-refresh every 60s so newly published menus appear without reload.
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const available = data?.available;
  const pdfSrc = `${PDF_URL}?v=${bust}`;
  const logo = brand.logo_text || brand.restaurant_name || 'La Contadina';

  return (
    <div className="min-h-screen bg-[#F9F9F7] text-[#1C1C19]" data-testid="tageskarte-page">
      {/* Header */}
      <header className="bg-[#1C1C19] text-white">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="font-display text-2xl tracking-tight">
            {logo}<span className="text-[#EAB308]">.</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Tageskarte</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <div className="flex items-center gap-2 mb-1">
          <UtensilsCrossed className="w-5 h-5 text-[#B45309]" />
          <p className="text-xs uppercase tracking-[0.2em] text-[#B45309]">Spezialitäten des Tages</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-[#1C1C19] leading-tight mb-2" data-testid="tageskarte-title">
          {data?.title || 'Tageskarte'}
        </h1>
        {available && data?.updated_at && (
          <p className="text-sm text-[#5C5C59] mb-6" data-testid="tageskarte-updated">
            Aktualisiert: {formatUpdated(data.updated_at)}
          </p>
        )}

        {loading ? (
          <div className="py-20 text-center text-[#5C5C59]" data-testid="tageskarte-loading">
            <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin opacity-50" />
            Lädt…
          </div>
        ) : !available ? (
          <div
            className="bg-white border border-dashed border-[#E5E5E0] rounded-xl p-10 text-center"
            data-testid="tageskarte-empty"
          >
            <FileText className="w-12 h-12 mx-auto mb-4 text-[#D4D4D0]" />
            <p className="font-display text-xl text-[#1C1C19] mb-1">Die Tageskarte wird gerade aktualisiert.</p>
            <p className="text-sm text-[#5C5C59]">Bitte schauen Sie in Kürze noch einmal vorbei.</p>
            <button
              onClick={load}
              data-testid="tageskarte-retry"
              className="mt-5 inline-flex items-center gap-2 border border-[#1C1C19] text-[#1C1C19] px-4 py-2 rounded-full text-sm hover:bg-[#1C1C19] hover:text-white transition"
            >
              <RefreshCw className="w-4 h-4" /> Neu laden
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Action buttons (always visible, reliable on mobile) */}
            <div className="flex flex-wrap gap-3">
              <a
                href={pdfSrc}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tageskarte-open"
                className="inline-flex items-center gap-2 bg-[#1C1C19] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#3A3A35] transition"
              >
                <ExternalLink className="w-4 h-4" /> Tageskarte öffnen (PDF)
              </a>
              <a
                href={`${PDF_URL}?download=1`}
                data-testid="tageskarte-download"
                className="inline-flex items-center gap-2 border border-[#E5E5E0] text-[#1C1C19] px-5 py-2.5 rounded-full text-sm font-medium hover:border-[#1C1C19] transition"
              >
                <Download className="w-4 h-4" /> Herunterladen
              </a>
            </div>

            {/* Embedded PDF (desktop & most mobile browsers) */}
            <div className="bg-white border border-[#E5E5E0] rounded-xl overflow-hidden" data-testid="tageskarte-viewer">
              <object data={pdfSrc} type="application/pdf" className="w-full h-[70vh] min-h-[420px]">
                <iframe title="Tageskarte" src={pdfSrc} className="w-full h-[70vh] min-h-[420px] border-0" />
              </object>
            </div>

            {(data?.chef_notes || data?.allergens) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data?.chef_notes && (
                  <div className="bg-white border border-[#E5E5E0] rounded-xl p-4" data-testid="tageskarte-chef-notes">
                    <div className="text-[10px] uppercase tracking-wider text-[#B45309] mb-1.5 font-medium">Hinweis des Küchenchefs</div>
                    <p className="text-sm text-[#1C1C19] whitespace-pre-line leading-relaxed">{data.chef_notes}</p>
                  </div>
                )}
                {data?.allergens && (
                  <div className="bg-white border border-[#E5E5E0] rounded-xl p-4" data-testid="tageskarte-allergens">
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-1.5 font-medium">Allergene</div>
                    <p className="text-sm text-[#1C1C19] whitespace-pre-line leading-relaxed">{data.allergens}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
